/**
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const NatObj = require('../types/NatObj')
const Color = require('../types/Color')
const StyleRemplissage = require('../types/StyleRemplissage')
const StyleTrait = require('../types/StyleTrait')
const CPolygone = require('./CPolygone')
const CImplementationProto = require('./CImplementationProto')
const CLigneBrisee = require('./CLigneBrisee')
const CListeObjets = require('./CListeObjets')
const CMacroAvecListe = require('./CMacroAvecListe')
const CSurfacePolygone = require('./CSurfacePolygone')
const CNoeudPointeurSurPoint = require('./CNoeudPointeurSurPoint')
const CSousListeObjets = require('./CSousListeObjets')
module.exports = CMacroConstructionRecursive

/**
 * Macro lançant une construction récursive.
 * @constructor
 * @extends CMacroAvecListe
 * @param {CListeObjets} listeProprietaire : La liste propriétaire.
 * @param {CImplementationProto} impProto : null ou la construction propriétaire.
 * @param {Boolean} estElementFinal : true si l'objet est un élément final de construction.
 * @param {Color} couleur : La couleur d'éciture de l'éditeur (et du cadre éventuel).
 * @param {Float} x_nom : L'abscisse d'affichage de la macro
 * @param {Float} y_nom : L'ordonnée d'affichage de la macro
 * @param {Integer} dec_x : Décalage horizontal du nom
 * @param {Integer} dec_y : Décalage vertical du nom
 * @param {Boolean} masque : true si l'éditeur est masqué
 * @param {CPt} pointLie : null ou pointe sur un point auquel l'affichage est lié.
 * @param {Integer} taillePolice : Indice de la taiile de police utilisée
 * @param {Boolean} effacementFond : true si on efface le fond de l'en-tête.
 * @param {Color} couleurFond : La couleur de fond de l'en-tête.
 * @param {Integer} alignementHorizontal : 0 pour alignement gauche, 1 pour centre, 2 pour droite.
 * @param {Integer} alignementVertical : 0 pour alignement vers le haut, 1 pour centré, 2 pour bas.
 * @param {String} intitule : Le titre de la macro
 * @param {String} commentaireMacro : Eventuel commentaire explicatif.
 * @param {CListeObjets} listeAssociee : liste contenant les éléments à utiliser comme objets sources
 * pour la première itération.
 * @param {Array} tableProto : Array of CPrototype qui donnent la construction à utiliser suivant le
 * niveau de récursion en cours.
 * @param {Integer} pasRec : Le nombre d'objets à sauter avant de réimpleménter la construction
 * dans un même niveau de récursion.
 * @param {Integer} nbImp : Le nombre de fois que la construction doit être imlémentée
 * dans un même niveau de récursion.
 * @param {Integer} nbSourcesPourIter : Le nombre d'objets sources qui sont communs à toules
 * les implémentations de construction.
 * @param {Boolean} creationNiveauMax : true si on ne crée les objets finaux que pour le niveau
 * maximum de récursions
 * @param {Boolean} imposerCouleur : si true, tous les objets finaux devront prendre la couleur couleurImp.
 * @param {Color} couleurImp : La couleur des objets finaux si imposerCouleur est true.
 * @param {Integer} choixLigne : Si 0, les points finaux ne sont pas reliés, si 1 ils sont reliés
 * par un polygone et si 2 reliés par une ligne brisée.
 * @param {StyleTrait} styleLigne : Le style de trait utilisé si choixLigne n'est pas 0.
 * @param {Color} couleurLigne : La couleur utilisé pour la ligne si choixLigne n'est pas 0.
 * @param {Boolean} polygoneRempli : trus si choixLigne vaut 1 et qu'on souhaite que le polygone soit rempli.
 */
function CMacroConstructionRecursive (listeProprietaire, impProto, estElementFinal, couleur,
    x_nom, y_nom, dec_x, dec_y, masque, pointLie, taillePolice, effacementFond, couleurFond, alignementHorizontal,
    alignementVertical, intitule, commentaireMacro, listeAssociee, tableProto, pasRec,
    nbImp, nbSourcesPourIter, creationNiveauMax, imposerCouleur, couleurImp, choixLigne, styleLigne,
    couleurLigne, polygoneRempli) {
  if (arguments.length !== 0) {
    if (arguments.length === 1) CMacroAvecListe.call(this, listeProprietaire);
    else {
      CMacroAvecListe.call(this, listeProprietaire, impProto, estElementFinal, couleur,
        x_nom, y_nom, dec_x, dec_y, masque, pointLie, taillePolice, effacementFond, couleurFond,
        alignementHorizontal, alignementVertical, intitule, commentaireMacro, listeAssociee);
      this.tableProto = tableProto;
      this.profondMax = tableProto.length - 1;
      this.pasRec = pasRec;
      this.nbImp = nbImp;
      this.nbSourcesPourIter = nbSourcesPourIter;
      this.creationNiveauMax = creationNiveauMax;
      this.imposerCouleur = imposerCouleur;
      this.couleurImp = couleurImp;
      this.choixLigne = choixLigne;
      this.styleLigne = styleLigne;
      this.couleurLigne = couleurLigne;
      this.polygoneRempli = polygoneRempli;
    }
  }
  this.dejaExecute = false;
}
CMacroConstructionRecursive.prototype = new CMacroAvecListe();
CMacroConstructionRecursive.prototype.constructor = CMacroConstructionRecursive;
CMacroConstructionRecursive.prototype.superClass = "CMacroAvecListe";
CMacroConstructionRecursive.prototype.className = "CMacroConstructionRecursive";

CMacroConstructionRecursive.prototype.getClone = function(listeSource, listeCible) {
  var ind1 = listeSource.indexOf(this.pointLie);
  var ind2 = listeSource.indexOf(this.impProto);
  var listeAssocieeClone = new CSousListeObjets();
  listeAssocieeClone.setImage(this.listeAssociee, listeSource, listeCible);
  return new CMacroConstructionRecursive(listeCible, listeCible.get(ind2, "CImplementationProto"),
    this.estElementFinal, this.couleur, this.x_nom, this.y_nom, this.dec_x, this.dec_y, this.masque,
    listeCible.get(ind1, "CPt"), this.taillePolice,
    this.effacementFond, this.couleurFond, this.alignementHorizontal, this.alignementVertical,
    this.intitule, this.commentaireMacro, listeAssocieeClone, this.tableProto, this.pasRec,this.nbImp,
    this.nbSourcesPourIter, this.creationNiveauMax, this.imposerCouleur, this.couleurImp, this.choixLigne,
    this.styleLigne, this.couleurLigne, this.polygoneRempli);
};
// Version 4.9.7 : On rajoute un paramètre affichage. Les objets ne sont affichés que si le paramètre est présent et true
CMacroConstructionRecursive.prototype.execute = function(svg, dimf, couleurFond, transparence, affichage) {
  	// On implémente la construction de façon itérative
  	// La liste associée contient les pointeurs sur les objets sources
  	// On affecte ces éléments aux objets sources du prototype
    var i,j,proto,liste,listePoints,impProto,listeObjetsSourcesConst,eg,listeObjetsSourcesVar,gen,
      indprot,profond,proto0,poly,ligne,elb;
		proto0 = this.tableProto[0];
  	for (j = 0; j <= this.profondMax; j++) {
  		proto = this.tableProto[j];
	  	for (i = 0; i < proto.nbObjSources; i++) proto.get(i).elementAssocie = this.listeAssociee.get(i);
  	}
		liste = this.listeProprietaire;
    listePoints = (this.choixLigne === 0) ? null : []; // Pour joindre éventuellement les points de dernière génération
		// Version 4.8 : Dans le cas où une (ou plusieurs) implémentation itérative de la construction a déjà été faite, lorsque de nouveaux points nommés
		// ou calculs sont créés il faut leur générer un nom. Pour éviter de gros ralentissements lors de l'implémentation, il faut générer ces
		// noms avec un suffixe numérique qui commence au nombre d'implémentations plus 1.
		// CLongueur ancienneLongueurUnite = liste.pointeurLongueurUnite;
		impProto = new CImplementationProto(liste, proto0);
		// On crée une liste formée des objets sources fixes et une autre formée des objets sources variables.
		listeObjetsSourcesConst = new CListeObjets();
		for (i = 0; i < this.nbSourcesPourIter; i++) {
			eg = proto0.get(i);
			listeObjetsSourcesConst.add(eg.elementAssocie);
		}
		listeObjetsSourcesVar = new CListeObjets();
		for (i = this.nbSourcesPourIter; i < proto0.nbObjSources; i++) {
			gen = proto0.get(i);
			listeObjetsSourcesVar.add(gen.elementAssocie);
		}
		indprot = liste.longueur();
		profond = 0;
		impProto.implementeRecursif(dimf, liste, this.tableProto, listeObjetsSourcesConst, listeObjetsSourcesVar,
      this.pasRec, this.nbImp, this.creationNiveauMax, profond, this.profondMax, listePoints);
		liste.traiteImplementationsProtoSuccessivesRecur(indprot, proto0);
		// liste.reconstruitChainesCalcul(indprot + 1, indprot + proto0.longueur() - proto0.nbObjSources);
		if (this.imposerCouleur) {
	    for (i= liste.indexOf(impProto)+1; i < liste.longueur(); i++) {
	    	elb = liste.get(i);
	    	if (elb.estElementFinal && (elb.estDeNature(NatObj.NTtObj))) {
	    		elb.donneCouleur(this.couleurImp);
	    	}
	    }
    }
		// On regarde si on moins un des objets finaux créés existe
    // Si demandé un polygone ou une ligne on joint tous les points créés.
    if (this.choixLigne !== 0) {
	    if (this.choixLigne === 1) {
				listePoints.push(new CNoeudPointeurSurPoint(liste, listePoints[0].pointeurSurPoint));
				poly = new CPolygone(liste, null, false, this.couleurLigne, false, this.styleLigne, listePoints);
				liste.add(poly);
				poly.positionne(false, dimf);
        if (this.polygoneRempli) {
          var s = new CSurfacePolygone(liste, null, false, this.couleurLigne, false,
            StyleRemplissage.quadrillagePleinTransp, poly);
          s.positionne(false, dimf);
          liste.add(s);
        }
	    }
	    else {
				ligne = new CLigneBrisee(liste, null, false, this.couleurLigne, false, this.styleLigne, listePoints);
				liste.add(ligne);
				ligne.positionne(false, dimf);
	    }
    }
  if ((arguments.length<=4) || affichage) this.listeProprietaire.afficheTout(indprot, svg, true, couleurFond, transparence); // Pour créer les éléments graphiques
  this.termineAction(svg, dimf, couleurFond, transparence);
  this.dejaExecute = true;
  // Spécial JavaScript. Dans la version Java c'est fait lors de la sauvegarde de la figure
  this.listeProprietaire.etablitListesInternesMacros();
};

CMacroConstructionRecursive.prototype.executionPossible = function() {
  return !this.dejaExecute;
};

CMacroConstructionRecursive.prototype.read = function(inps, list){
  var i,indProto,r,g,b;
  CMacroAvecListe.prototype.read.call(this, inps, list);
  this.profondMax = inps.readInt();
  this.tableProto = [];
  for (i = 0; i <= this.profondMax; i++) {
    indProto = inps.readInt();
    this.tableProto.push(list.documentProprietaire.tablePrototypes[indProto]);
  }
  this.pasRec = inps.readInt();
  this.nbImp = inps.readInt();
  this.nbSourcesPourIter = inps.readInt();
  this.creationNiveauMax = inps.readBoolean();
  this.imposerCouleur = inps.readBoolean();
  if (this.imposerCouleur) {
    r = inps.readByte();
    g = inps.readByte();
    b = inps.readByte();
    this.couleurImp = new Color(r, g, b);
  }
  this.choixLigne = inps.readInt();
  if (this.choixLigne !== 0) {
    r = inps.readByte();
    g = inps.readByte();
    b = inps.readByte();
    this.couleurLigne = new Color(r, g, b);
    this.styleLigne = new StyleTrait(list);
    if (list.numeroVersion < 15) this.styleLigne.readOld(inps, list, false);
      else this.styleLigne.read(inps, list);
    if (this.choixLigne === 1) // Polygone
      this.polygoneRempli = inps.readBoolean();
  }
};
CMacroConstructionRecursive.prototype.write = function(oups, list){
  CMacroAvecListe.prototype.write.call(this, oups, list);
  oups.writeInt(this.profondMax);
  // On enregistre l'indice du prototype utilisé dans la table des prototype du document
  for (var i = 0; i <= this.profondMax; i++) oups.writeInt(list.documentProprietaire.
    tablePrototypes.indexOf(this.tableProto[i]));
  oups.writeInt(this.pasRec);
  oups.writeInt(this.nbImp);
  oups.writeInt(this.nbSourcesPourIter);
  oups.writeBoolean(this.creationNiveauMax);
  oups.writeBoolean(this.imposerCouleur);
  if (this.imposerCouleur) {
    oups.writeByte(this.couleurImp.getRed());
    oups.writeByte(this.couleurImp.getGreen());
    oups.writeByte(this.couleurImp.getBlue());
  }
  oups.writeInt(this.choixLigne);
  if (this.choixLigne !== 0) {
    oups.writeByte(this.couleurLigne.getRed());
    oups.writeByte(this.couleurLigne.getGreen());
    oups.writeByte(this.couleurLigne.getBlue());
    oups.writeInt(this.styleLigne);
    if (this.styleLigne === 1) oups.writeBoolean(this.polygoneRempli);
  }
};
